import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plane, Bus, Car, ChevronDown, ExternalLink } from 'lucide-react';
import { renderTravelBodyLines } from '../../config/travelGuide';
import './TravelGuide.css';

const GUIDE_ICONS = {
  fly: Plane,
  bus: Bus,
  drive: Car,
};

function linkifyLine(line, keyPrefix) {
  const parts = [];
  const re = /(https?:\/\/[^\s]+)/g;
  let last = 0;
  let match;
  let i = 0;
  while ((match = re.exec(line)) !== null) {
    if (match.index > last) {
      parts.push(<span key={`${keyPrefix}-t${i}`}>{line.slice(last, match.index)}</span>);
    }
    const url = match[1].replace(/[.,);]+$/, '');
    const trailing = match[1].slice(url.length);
    parts.push(
      <a
        key={`${keyPrefix}-a${i}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="travel-guide-link"
      >
        {url.replace(/^https?:\/\//, '')}
        <ExternalLink size={12} aria-hidden="true" />
      </a>
    );
    if (trailing) parts.push(<span key={`${keyPrefix}-tr${i}`}>{trailing}</span>);
    last = match.index + match[1].length;
    i += 1;
  }
  if (last < line.length) {
    parts.push(<span key={`${keyPrefix}-end`}>{line.slice(last)}</span>);
  }
  return parts.length ? parts : line;
}

const AccordionItem = ({ section, open, onToggle, reduceMotion }) => {
  const lines = renderTravelBodyLines(section.body);

  return (
    <div className={`travel-acc-item${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="travel-acc-header"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span>{section.title}</span>
        <ChevronDown size={20} className="travel-acc-chevron" aria-hidden="true" />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className="travel-acc-panel"
            key="panel"
            initial={reduceMotion ? false : { height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={reduceMotion ? undefined : { height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="travel-acc-body">
              {lines.map((line, idx) => {
                if (line.trim() === '') {
                  return <div key={idx} className="travel-acc-spacer" />;
                }
                return (
                  <p key={idx} className="travel-acc-line">
                    {linkifyLine(line, `${section.id}-${idx}`)}
                  </p>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const TravelGuide = ({ guide, reduceMotion = false }) => {
  const guides = guide?.guides || [];
  const [activeId, setActiveId] = useState(guides[0]?.id || '');
  const [openSectionId, setOpenSectionId] = useState(null);

  useEffect(() => {
    if (!guides.find((g) => g.id === activeId) && guides[0]) {
      setActiveId(guides[0].id);
    }
  }, [guides, activeId]);

  useEffect(() => {
    setOpenSectionId(null);
  }, [activeId]);

  const active = guides.find((g) => g.id === activeId) || guides[0];
  if (!active) {
    return <p className="travel-guide-empty">Travel details will appear here soon.</p>;
  }

  return (
    <div className="travel-guide">
      <div className="travel-guide-tabs" role="tablist" aria-label="Travel options">
        {guides.map((g) => {
          const Icon = GUIDE_ICONS[g.id] || Plane;
          const selected = g.id === active.id;
          return (
            <button
              key={g.id}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-current={selected ? 'true' : undefined}
              className={`travel-guide-tab site-card${selected ? ' is-active' : ''}`}
              onClick={() => setActiveId(g.id)}
            >
              {selected && <span className="travel-guide-tab-badge">Selected</span>}
              <span className="travel-guide-tab-icon">
                <Icon size={22} strokeWidth={1.75} />
              </span>
              <span className="travel-guide-tab-text">
                <strong>{g.title}</strong>
                {g.short_description ? <span>{g.short_description}</span> : null}
              </span>
            </button>
          );
        })}
      </div>

      <div className="travel-guide-detail site-card" role="tabpanel">
        {active.intro && <p className="travel-guide-intro">{active.intro}</p>}

        <div className="travel-acc-list">
          {(active.sections || []).map((section) => (
            <AccordionItem
              key={section.id}
              section={section}
              open={openSectionId === section.id}
              onToggle={() =>
                setOpenSectionId((prev) => (prev === section.id ? null : section.id))
              }
              reduceMotion={reduceMotion}
            />
          ))}
        </div>

        {guide.closing_note && (
          <p className="travel-guide-closing">{guide.closing_note}</p>
        )}
      </div>
    </div>
  );
};

export default TravelGuide;
